import { Button, Field, Select, Spin } from '@/components/commons';
import { APP_ROUTER } from '@/routes/routes';
import { AyumiService } from '@/services/ayumi';
import { OfferService } from '@/services/offer';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import {
  AYUMI_TOPIC_STATUS_LABELS,
  AyumiTopicStatus,
} from '@/types/common/ayumi';
import {
  ICreateAyumiTopicRequest,
  IUpdateAyumiTopicKnowRequest,
} from '@/types/request';
import { IGetDetailAyumiResponse, IOfferOption } from '@/types/response';
import {
  NOTIFY_STATUS,
  notificationMessage,
  notificationModal,
} from '@/utils';
import { LeftCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Tabs, Tag } from 'antd';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { AYUMI_TAB, AYUMI_TAB_LABELS, FORM } from './constants';
import { formSchemaFn } from './formSchemaFn';
import { KnowTab } from './KnowTab';
import { PrepareTab } from './PrepareTab';

interface IProps {
  title?: string;
}

/** オファー候補の取得上限。一覧 API を 1 ページで読み切るための値 */
const OFFER_OPTION_LIMIT = 200;

/**
 * ステータス切り替えボタン。
 * 「下書き保存」だと入力内容が保存されると読めるため「下書きにする」と書く
 * （他の2つと語尾を揃え、3つとも状態を切り替えるだけのボタンだと分かるようにする）。
 */
const STATUS_ACTIONS: {
  status: AyumiTopicStatus;
  label: string;
  primary?: boolean;
}[] = [
  { status: AyumiTopicStatus.PUBLIC, label: '公開する', primary: true },
  { status: AyumiTopicStatus.DRAFT, label: '下書きにする' },
  { status: AyumiTopicStatus.HIDDEN, label: '非表示にする' },
];

const STATUS_TAG_COLORS: Record<AyumiTopicStatus, string> = {
  [AyumiTopicStatus.PUBLIC]: 'green',
  [AyumiTopicStatus.DRAFT]: 'default',
  [AyumiTopicStatus.HIDDEN]: 'orange',
};

/** オファー entity の下書き。一覧 API は日本語に解決済みだが、課題詳細に乗るオファーは数値のまま */
const OFFER_STATUS_DRAFT = 0;

/**
 * オファー名の頭に付ける公開状態。
 * 一覧 API の `status` は 公開 / 公開予定 / 非公開 に解決済みだが公開終了を見ていないため、
 * 期間切れはここで判定する。「公開」は「公開終了」と紛らわしいので「公開中」と出す。
 */
const offerStatusLabel = (offer: IOfferOption) => {
  if (offer.status === '非公開' || offer.status === OFFER_STATUS_DRAFT) {
    return '非公開';
  }

  if (offer.status === '公開予定') return '公開予定';

  if (offer.publicAt && moment(offer.publicAt).isAfter(moment())) {
    return '公開予定';
  }

  if (offer.publicEndAt && moment(offer.publicEndAt).isBefore(moment())) {
    return '公開終了';
  }

  return '公開中';
};

/** プルダウンの表示は `ステータス：タイトル` */
const offerOptionLabel = (offer: IOfferOption) =>
  `${offerStatusLabel(offer)}：${offer.title}`;

/** 開いていないタブの保存漏れに気付けるよう、未保存のタブには見出しに印を付ける */
const tabLabel = (tab: AYUMI_TAB, isDirty: boolean) => (
  <span>
    {AYUMI_TAB_LABELS[tab]}
    {isDirty && (
      <span className='ml-1 text-[#d46b08]' title='未保存の変更があります'>
        ●
      </span>
    )}
  </span>
);

export const AyumiForm: React.FC<IProps> = ({ title }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const topicId = id ? Number(id) : undefined;
  const formName = topicId ? FORM.UPDATE_AYUMI : FORM.CREATE_AYUMI;

  const [detail, setDetail] = useState<IGetDetailAyumiResponse>();
  const [activeTab, setActiveTab] = useState<AYUMI_TAB>(AYUMI_TAB.KNOW);
  // 「そなえる」のカードは1枚ずつ別フォームなので、未保存のカードを id で集めておく
  const [dirtyCategoryKeys, setDirtyCategoryKeys] = useState<string[]>([]);
  const [isVideoUploading, setIsVideoUploading] = useState(false);

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(formName)),
    defaultValues: { topicKey: undefined, summary: '', questions: [] },
  });

  const { handleSubmit, reset, formState } = methods;
  /** 「課題を知る」タブに未保存の入力があるか */
  const isKnowDirty = formState.isDirty;
  const isPrepareDirty = dirtyCategoryKeys.length > 0;
  const hasUnsavedChanges = isKnowDirty || isPrepareDirty;

  const handleCategoryDirtyChange = useCallback(
    (key: string, isDirty: boolean) =>
      setDirtyCategoryKeys((prev) => {
        // 同じ内容なら参照ごと返して余計な再レンダーを起こさない
        if (prev.includes(key) === isDirty) return prev;

        return isDirty ? [...prev, key] : prev.filter((item) => item !== key);
      }),
    [],
  );

  const { isFetching, refetch } = useQuery(
    [ENDPOINT.AYUMI.GET_DETAIL, topicId],
    () => AyumiService.getDetail(topicId!),
    {
      enabled: !!topicId,
      onSuccess: (response: IResponse<IGetDetailAyumiResponse>) => {
        const data = response?.data;
        setDetail(data);
        reset({
          summary: data.summary ?? '',
          videoUrl: data.videoUrl ?? '',
          videoPosterUrl: data.videoPosterUrl ?? '',
          // numeric は文字列で返るのでフォーム側で数値に戻す
          videoDurationSec:
            data.videoDurationSec != null ? Number(data.videoDurationSec) : null,
          videoWidth: data.videoWidth ?? null,
          videoHeight: data.videoHeight ?? null,
          knowTitle: data.knowTitle ?? '',
          knowBody: data.knowBody ?? '',
          knowImageUrl: data.knowImage?.url ?? '',
          questions: (data.questions ?? []).map((question) => ({
            id: question.id,
            question: question.question,
            answerLabelSet: question.answerLabelSet,
            explanation: question.explanation ?? '',
          })),
        });
      },
      onError: () => navigate(APP_ROUTER.NOT_FOUND),
    },
  );

  /** 新規作成のプルダウン候補。同じ課題を何件でも作れるため作成済みでも選べる */
  const { data: topicKeys, isFetching: isFetchingKeys } = useQuery(
    [ENDPOINT.AYUMI.GET_KEYS],
    () => AyumiService.getTopicKeys(),
    { enabled: !topicId },
  );

  const topicKeyOptions = (topicKeys?.data ?? []).map((option) => ({
    value: option.topicKey,
    label: option.name,
  }));

  /**
   * 民間サービス項目の「紐づけるオファー」候補。
   * 入稿は公開前に行うため、公開中のオファーだけを返す `/offers/find` ではなく一覧 API を使い、
   * 非公開・公開予定・公開終了のオファーもタイトルで選べるようにする。
   */
  const { data: offers } = useQuery(
    [ENDPOINT.OFFER.GET_LIST, OFFER_OPTION_LIMIT],
    () => OfferService.getList({ page: 1, limit: OFFER_OPTION_LIMIT }),
    { enabled: !!topicId },
  );

  const offerOptions = useMemo(() => {
    const options = (offers?.data?.data ?? []).map((offer) => ({
      value: offer.id,
      label: offerOptionLabel(offer),
    }));
    const listed = new Set(options.map((option) => option.value));

    // 候補に載らなかった紐づけ済みオファー（上限超え・削除済み）も、id が生で出ないよう補う
    (detail?.categories ?? []).forEach((category) =>
      category.items.forEach((item) => {
        if (item.offerId && item.offer && !listed.has(item.offerId)) {
          listed.add(item.offerId);
          options.push({
            value: item.offerId,
            label: offerOptionLabel(item.offer),
          });
        }
      }),
    );

    return options;
  }, [offers, detail]);

  const { mutate: create, isLoading: isCreating } = useMutation(
    (body: ICreateAyumiTopicRequest) => AyumiService.create(body),
    {
      onSuccess: (response: IResponse<IGetDetailAyumiResponse>) => {
        notificationMessage({
          message: '課題を作成しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        navigate(
          APP_ROUTER.AYUMI.UPDATE.replace(':id', String(response?.data?.id)),
        );
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: '課題の作成に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
        console.error(error);
      },
    },
  );

  const { mutate: updateKnow, isLoading: isSaving } = useMutation(
    (body: IUpdateAyumiTopicKnowRequest) =>
      AyumiService.updateKnow(body, topicId!),
    {
      onSuccess: () => {
        notificationMessage({
          message: '保存しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
      },
      onError: () => {
        notificationMessage({
          message: '保存に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const { mutate: updateStatus, isLoading: isUpdatingStatus } = useMutation(
    (status: AyumiTopicStatus) =>
      AyumiService.updateStatus({ status }, topicId!),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'ステータスを変更しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
      },
      onError: () => {
        notificationMessage({
          message: 'ステータスの変更に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const { mutate: remove, isLoading: isDeleting } = useMutation(
    () => AyumiService.delete(topicId!),
    {
      onSuccess: () => {
        notificationMessage({
          message: '課題を削除しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        navigate(APP_ROUTER.AYUMI.LIST);
      },
      onError: () => {
        notificationMessage({
          message: '課題の削除に失敗しました。',
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const handleCreate = (values: any) => create({ topicKey: values.topicKey });

  /**
   * ステータス変更はタブの入力内容を保存しない。
   * 「下書きにする」を保存操作だと思ったまま入力を捨ててしまわないよう、未保存なら確認する
   */
  const handleChangeStatus = (status: AyumiTopicStatus) => {
    if (!hasUnsavedChanges) {
      updateStatus(status);
      return;
    }

    const target = [
      isKnowDirty ? `「${AYUMI_TAB_LABELS[AYUMI_TAB.KNOW]}」タブ` : '',
      isPrepareDirty
        ? `「${AYUMI_TAB_LABELS[AYUMI_TAB.PREPARE]}」タブのカテゴリ`
        : '',
    ]
      .filter(Boolean)
      .join('と');

    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '未保存の入力があります',
      content: `${target}に未保存の入力があります。ステータスの変更では入力内容は保存されません。入力を保存せずにステータスだけ変更しますか？`,
      okText: 'ステータスだけ変更',
      cancelText: 'キャンセル',
      maskClosable: false,
      onOk: () => updateStatus(status),
    });
  };

  /**
   * 「課題を知る」タブの保存。
   *
   * 画像と動画の URL は空文字をそのまま送る。`|| undefined` にすると JSON から
   * 落ちてサーバーが「触らない」と解釈し、一度入れた素材を二度と外せなくなる。
   * 尺・寸法も同じ理由で null のまま送る。URL を差し替えたときに前の動画の値が残らない。
   */
  const handleSaveKnow = (values: any) => {
    updateKnow({
      summary: values.summary || undefined,
      videoUrl: values.videoUrl ?? '',
      videoPosterUrl: values.videoPosterUrl ?? '',
      videoDurationSec:
        values.videoDurationSec != null ? Number(values.videoDurationSec) : null,
      videoWidth: values.videoWidth ?? null,
      videoHeight: values.videoHeight ?? null,
      knowTitle: values.knowTitle || undefined,
      knowBody: values.knowBody || undefined,
      knowImageUrl: values.knowImageUrl ?? '',
      questions: (values.questions ?? []).map((question: any) => ({
        ...(question.id ? { id: question.id } : {}),
        question: question.question,
        answerLabelSet: Number(question.answerLabelSet),
        explanation: question.explanation || undefined,
      })),
    });
  };

  // 新規作成は課題名だけ。中身は作成後にタブで埋める
  if (!topicId) {
    return (
      <div className='w-full md:w-[560px] mx-auto'>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <h3 className='font-bold text-[20px] mb-4'>課題管理｜新規作成</h3>
        <FormProvider {...methods}>
          <div className='flex flex-col gap-4 p-4 bg-white border rounded'>
            {/* 課題名は自由入力せず、7 種類の課題からプルダウンで選ぶ */}
            <Field label='課題' name='topicKey' required>
              <Select
                placeholder='課題を選択'
                loading={isFetchingKeys}
                options={topicKeyOptions}
              />
            </Field>
            <div className='flex gap-2'>
              <Button
                className='w-1/2'
                icon={<LeftCircleOutlined />}
                onClick={() => navigate(APP_ROUTER.AYUMI.LIST)}
              >
                戻る
              </Button>
              <Button
                className='w-1/2'
                type='primary'
                loading={isCreating}
                onClick={handleSubmit(handleCreate)}
              >
                作成
              </Button>
            </div>
          </div>
        </FormProvider>
      </div>
    );
  }

  return (
    <div className='w-full'>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <Spin spinning={isFetching}>
        <header className='mb-4'>
          <h3 className='font-bold text-[20px]'>
            課題編集：{detail?.name ?? ''}
          </h3>
          <p className='text-[11px] text-[#666]'>
            更新日{' '}
            {detail?.updatedAt
              ? moment(detail.updatedAt).format('YYYY.MM.DD')
              : '―'}
            　
            {detail
              ? AYUMI_TOPIC_STATUS_LABELS[detail.status as AyumiTopicStatus]
              : ''}
          </p>
        </header>

        <div className='flex flex-col gap-4 lg:flex-row lg:items-start'>
          <div className='flex-1 p-6 bg-white border rounded'>
            <FormProvider {...methods}>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as AYUMI_TAB)}
                items={[
                  {
                    key: AYUMI_TAB.KNOW,
                    label: tabLabel(AYUMI_TAB.KNOW, isKnowDirty),
                    children: (
                      <>
                        <KnowTab onVideoUploadingChange={setIsVideoUploading} />
                        <div className='flex items-center justify-end gap-3 mt-4'>
                          {isKnowDirty && (
                            <span className='text-[12px] font-bold text-[#d46b08]'>
                              未保存の変更があります
                            </span>
                          )}
                          <Button
                            type='primary'
                            icon={<SaveOutlined />}
                            loading={isSaving}
                            disabled={isVideoUploading}
                            onClick={handleSubmit(handleSaveKnow)}
                          >
                            「{AYUMI_TAB_LABELS[AYUMI_TAB.KNOW]}」を保存
                          </Button>
                        </div>
                      </>
                    ),
                  },
                  {
                    key: AYUMI_TAB.PREPARE,
                    label: tabLabel(AYUMI_TAB.PREPARE, isPrepareDirty),
                    children: (
                      <PrepareTab
                        topicId={topicId}
                        categories={detail?.categories ?? []}
                        offerOptions={offerOptions}
                        onChanged={refetch}
                        onDirtyChange={handleCategoryDirtyChange}
                      />
                    ),
                  },
                ]}
              />
            </FormProvider>
          </div>

          {/*
            ステータス変更と入力内容の保存は別物。同じ「アクション」の並びに混ざっていると
            「下書き保存」を保存操作だと読み違えるため、見出しで区切って注記を添える
          */}
          <aside className='w-full lg:w-[320px] p-5 bg-white border rounded'>
            <div className='flex items-center gap-2 mb-1'>
              <h4 className='font-bold text-[15px]'>ステータス</h4>
              {detail && (
                <Tag color={STATUS_TAG_COLORS[detail.status]} className='m-0'>
                  {AYUMI_TOPIC_STATUS_LABELS[detail.status]}
                </Tag>
              )}
            </div>
            <p className='mb-3 text-[11px] text-[#888]'>
              押すと即時反映されます。
              <span className='font-bold text-[#d46b08]'>
                タブの入力内容は保存されません。
              </span>
            </p>
            <div className='flex flex-col gap-2'>
              {STATUS_ACTIONS.map(({ status, label, primary }) => (
                <Button
                  key={status}
                  type={primary ? 'primary' : 'default'}
                  loading={isUpdatingStatus}
                  disabled={detail?.status === status}
                  onClick={() => handleChangeStatus(status)}
                >
                  {label}
                </Button>
              ))}
            </div>

            <h4 className='mt-5 mb-3 font-bold text-[15px]'>操作</h4>
            <div className='flex flex-col gap-2'>
              <Button
                danger
                loading={isDeleting}
                onClick={() =>
                  notificationModal({
                    type: NOTIFY_STATUS.CONFIRM,
                    title: '確認',
                    content:
                      'この課題と、配下の質問・カテゴリ・対策項目をすべて削除します。よろしいですか？',
                    maskClosable: false,
                    onOk: () => remove(),
                  })
                }
              >
                削除
              </Button>
              <Button
                icon={<LeftCircleOutlined />}
                onClick={() => navigate(APP_ROUTER.AYUMI.LIST)}
              >
                一覧に戻る
              </Button>
            </div>

            <p className='mt-4 pt-3 border-t text-[11px] text-[#888]'>
              入力内容の保存は各タブの保存ボタンで行います。「
              {AYUMI_TAB_LABELS[AYUMI_TAB.PREPARE]}
              」はカテゴリカードごとに保存が必要です。
            </p>
          </aside>
        </div>
      </Spin>
    </div>
  );
};
